import { create } from 'zustand';
import {
  DASHBOARD_LAYOUT_VERSION,
  type DashboardLayout,
  type PlacedWidget,
  type PersonaKey,
  type WidgetWidth,
  type NormalizeContext,
  getWidget,
  isWidgetEligible,
} from '@pcp/shared';

// ─── Dashboard layout store ───────────────────────────────────────────────────
// Mirrors the `useThemeStore` shape/philosophy (a small Zustand store), but is
// NOT persisted to localStorage — the SERVER is authoritative for the saved
// layout (GET/PUT /api/dashboard/layout, owned by the backend slice). This store
// holds:
//   • `server`  — the last layout the server confirmed (the "truth" snapshot).
//   • `draft`   — the working copy the edit UI mutates; `null` when not editing.
//   • `editing` — whether the dashboard is in edit mode.
//   • `previewBreakpoint` — which breakpoint the editor is previewing, so the
//     size-cycle / visibility toggles act on the right per-breakpoint fields and
//     both desktop+mobile sizes are editable without resizing the window.
//
// Discard restores `server`; Save PUTs the draft and ADOPTS the response as the
// new `server` truth (the server may have stripped ineligible widgets). All
// reads outside edit mode use `server`; inside edit mode the grid renders `draft`.

/** The breakpoint the editor is currently previewing (independent of viewport). */
export type PreviewBreakpoint = 'desktop' | 'mobile';

/** Result of a save attempt, so the caller can toast + roll back on failure. */
export interface SaveResult {
  ok: boolean;
  /** The server-confirmed layout on success (already adopted as truth). */
  layout?: DashboardLayout;
  error?: string;
}

const LAYOUT_PATH = '/api/dashboard/layout';

// The server route envelopes the layout as `{ data: { layout, configuredFeatures } }`
// and READS the incoming layout from `body.layout` (+ server-controlled
// `markOnboarded`). Both helpers below MUST match that contract: sending the layout
// fields at the top level made the server persist an EMPTY layout, and reading
// `data` (instead of `data.layout`) made the client adopt a non-layout object with
// no `.widgets` → a blank/crashing dashboard on save. This is the incident root cause.

/** Read the current saved layout from the server (dedicated route). */
export async function fetchLayout(): Promise<DashboardLayout | null> {
  const res = await fetch(LAYOUT_PATH, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Failed to load dashboard layout (${res.status})`);
  const body = (await res.json()) as { data?: { layout?: DashboardLayout | null } | null };
  return body.data?.layout ?? null;
}

/** PUT a layout to the server; returns the server-reconciled layout (truth). */
async function putLayout(
  layout: DashboardLayout,
  opts?: { markOnboarded?: boolean },
): Promise<DashboardLayout> {
  const payload: Record<string, unknown> = { layout };
  if (opts?.markOnboarded) payload.markOnboarded = true;
  const res = await fetch(LAYOUT_PATH, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { error?: string }).error || `Save failed (${res.status})`);
  }
  const body = (await res.json()) as { data: { layout: DashboardLayout } };
  return body.data.layout;
}

/** Build the default placement for a freshly-added widget (optimal per-breakpoint
 *  size + default visibility + default settings), mirroring the shared reconcile. */
function defaultPlacementFor(id: string): PlacedWidget | null {
  const d = getWidget(id);
  if (!d) return null;
  const pw: PlacedWidget = {
    id: d.id,
    widthDesktop: d.defaultWidthDesktop,
    widthMobile: d.defaultWidthMobile,
    visibleOnDesktop: d.defaultVisibleDesktop,
    visibleOnMobile: d.defaultVisibleMobile,
  };
  if (d.settings && d.settings.length > 0) {
    const s: Record<string, number | string | boolean> = {};
    for (const f of d.settings) s[f.key] = f.default;
    pw.settings = s;
  }
  return pw;
}

/** The general-purpose MINIMAL default layout — what "Reset" restores to: a small
 *  set of broadly-useful, always-eligible widgets (filtered by the caller's context
 *  so nothing ineligible slips in). Personas/density/onboardedAt are layered back on
 *  by the caller. Deliberately NOT persona-specific — it's the universal baseline. */
const MINIMAL_DEFAULT_IDS = ['stat-strip', 'new-azure', 'new-commits', 'recent-activity', 'quick-links', 'notes'];
export function minimalDefaultLayout(ctx: NormalizeContext): DashboardLayout {
  const widgets: PlacedWidget[] = [];
  for (const id of MINIMAL_DEFAULT_IDS) {
    const d = getWidget(id);
    if (!d || !isWidgetEligible(d, ctx)) continue;
    const pw = defaultPlacementFor(id);
    if (pw) widgets.push(pw);
  }
  return { version: DASHBOARD_LAYOUT_VERSION, personas: [], widgets };
}

/** Move an array item from `from` to `to` (returns a new array). */
function arrayMove<T>(arr: readonly T[], from: number, to: number): T[] {
  const next = arr.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function patchWidget(
  layout: DashboardLayout,
  id: string,
  patch: Partial<PlacedWidget>,
): DashboardLayout {
  return {
    ...layout,
    widgets: layout.widgets.map((w) => (w.id === id ? { ...w, ...patch } : w)),
  };
}

interface DashboardStoreState {
  server: DashboardLayout | null;
  draft: DashboardLayout | null;
  editing: boolean;
  previewBreakpoint: PreviewBreakpoint;
  /** A save is in flight (drives a busy state on the Save button). */
  saving: boolean;

  /** Adopt a server-confirmed layout as the new truth (after fetch or save). */
  setServer: (layout: DashboardLayout | null) => void;
  /** Enter edit mode: clone `server` into `draft`. No-op without a server layout. */
  beginEdit: () => void;
  /** Discard the draft and leave edit mode (restores `server`). */
  cancelEdit: () => void;
  /** Set which breakpoint the editor previews (Desktop ⇄ Mobile toggle). */
  setPreviewBreakpoint: (bp: PreviewBreakpoint) => void;

  // ── Draft mutations (no-ops when not editing) ──
  reorder: (fromIndex: number, toIndex: number) => void;
  /** Move a widget by id one slot up/down (keyboard reorder + move buttons). */
  move: (id: string, direction: -1 | 1) => void;
  /** Generic per-widget patch (width / visibility / settings) — the grid's
   *  `onWidgetChange` funnels here; the keys are already breakpoint-specific. */
  patchWidget: (id: string, patch: Partial<PlacedWidget>) => void;
  /** Set the width for the CURRENTLY-PREVIEWED breakpoint. */
  setWidth: (id: string, width: WidgetWidth) => void;
  /** Set per-breakpoint visibility explicitly. */
  setVisible: (id: string, breakpoint: PreviewBreakpoint, visible: boolean) => void;
  setSettings: (id: string, settings: Record<string, number | string | boolean>) => void;
  addWidget: (id: string) => void;
  removeWidget: (id: string) => void;
  /** Replace the whole draft (Reset-to-defaults / AI suggestion preview). */
  replaceDraft: (layout: DashboardLayout) => void;
  setPersonas: (personas: PersonaKey[]) => void;

  /** Persist the current draft; adopts the response as truth and exits edit mode. */
  save: () => Promise<SaveResult>;
}

export const useDashboardStore = create<DashboardStoreState>()((set, get) => ({
  server: null,
  draft: null,
  editing: false,
  previewBreakpoint: 'desktop',
  saving: false,

  // Defense-in-depth: never let a malformed payload (missing/!array widgets) reach
  // the renderer — coerce widgets to an array so the grid can't crash on iteration.
  setServer: (layout) =>
    set({
      server: layout
        ? { ...layout, widgets: Array.isArray(layout.widgets) ? layout.widgets : [] }
        : layout,
    }),

  beginEdit: () => {
    const { server } = get();
    if (!server) return;
    // Structured clone so draft mutations never touch the server snapshot.
    set({ editing: true, draft: structuredClone(server), previewBreakpoint: 'desktop' });
  },

  cancelEdit: () => set({ editing: false, draft: null }),

  setPreviewBreakpoint: (previewBreakpoint) => set({ previewBreakpoint }),

  reorder: (fromIndex, toIndex) =>
    set((s) => {
      if (!s.draft) return s;
      if (fromIndex === toIndex) return s;
      const widgets = s.draft.widgets;
      if (fromIndex < 0 || fromIndex >= widgets.length) return s;
      if (toIndex < 0 || toIndex >= widgets.length) return s;
      return { draft: { ...s.draft, widgets: arrayMove(widgets, fromIndex, toIndex) } };
    }),

  move: (id, direction) =>
    set((s) => {
      if (!s.draft) return s;
      const from = s.draft.widgets.findIndex((w) => w.id === id);
      if (from < 0) return s;
      const to = from + direction;
      if (to < 0 || to >= s.draft.widgets.length) return s;
      return { draft: { ...s.draft, widgets: arrayMove(s.draft.widgets, from, to) } };
    }),

  patchWidget: (id, patch) =>
    set((s) => (s.draft ? { draft: patchWidget(s.draft, id, patch) } : s)),

  setWidth: (id, width) =>
    set((s) => {
      if (!s.draft) return s;
      const key = s.previewBreakpoint === 'mobile' ? 'widthMobile' : 'widthDesktop';
      return { draft: patchWidget(s.draft, id, { [key]: width } as Partial<PlacedWidget>) };
    }),

  setVisible: (id, breakpoint, visible) =>
    set((s) => {
      if (!s.draft) return s;
      const key = breakpoint === 'mobile' ? 'visibleOnMobile' : 'visibleOnDesktop';
      return { draft: patchWidget(s.draft, id, { [key]: visible } as Partial<PlacedWidget>) };
    }),

  setSettings: (id, settings) =>
    set((s) => (s.draft ? { draft: patchWidget(s.draft, id, { settings }) } : s)),

  addWidget: (id) =>
    set((s) => {
      if (!s.draft) return s;
      if (s.draft.widgets.some((w) => w.id === id)) return s; // already placed
      const placement = defaultPlacementFor(id);
      if (!placement) return s;
      return { draft: { ...s.draft, widgets: [...s.draft.widgets, placement] } };
    }),

  removeWidget: (id) =>
    set((s) =>
      s.draft
        ? { draft: { ...s.draft, widgets: s.draft.widgets.filter((w) => w.id !== id) } }
        : s,
    ),

  replaceDraft: (layout) => set((s) => (s.editing ? { draft: structuredClone(layout) } : s)),

  setPersonas: (personas) =>
    set((s) => (s.draft ? { draft: { ...s.draft, personas } } : s)),

  save: async () => {
    const { draft } = get();
    if (!draft) return { ok: false, error: 'Nothing to save.' };
    set({ saving: true });
    try {
      const confirmed = await putLayout(draft);
      // Adopt the server-reconciled layout as the new truth (it may have stripped
      // ineligible widgets), then leave edit mode.
      set({ server: confirmed, draft: null, editing: false, saving: false });
      return { ok: true, layout: confirmed };
    } catch (e) {
      // Roll back: keep the draft so the user can retry; stay in edit mode.
      set({ saving: false });
      return { ok: false, error: e instanceof Error ? e.message : 'Save failed.' };
    }
  },
}));

// DEV-only: expose the store on window for local QA/repro (e.g. injecting a layout
// to render every widget without a backend). Tree-shaken from prod (import.meta.env.DEV).
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __dashStore?: typeof useDashboardStore }).__dashStore = useDashboardStore;
}

/** Persist a layout out-of-band (wizard finish), adopting the result as truth.
 *  Used by the onboarding slice's single-PUT finish (markOnboarded). */
export async function persistLayout(
  layout: DashboardLayout,
  opts?: { markOnboarded?: boolean },
): Promise<DashboardLayout> {
  const confirmed = await putLayout(layout, opts);
  useDashboardStore.getState().setServer(confirmed);
  return confirmed;
}

/** Re-run the setup wizard: PUT the current layout with `onboardedAt` STRIPPED and
 *  WITHOUT markOnboarded, so the server clears it (the PUT only re-stamps it when
 *  markOnboarded is true). The OnboardingGate then shows the wizard again on the
 *  next profile read. Self-scoped, single-writer (the same dedicated PUT route). */
export async function resetOnboarding(): Promise<DashboardLayout> {
  const state = useDashboardStore.getState();
  const current = state.server;
  // Carry forward the user's personas/density/widgets but drop onboardedAt.
  const base: DashboardLayout = current
    ? { version: current.version, personas: current.personas, density: current.density, widgets: current.widgets }
    : { version: DASHBOARD_LAYOUT_VERSION, personas: [], widgets: [] };
  const confirmed = await putLayout(base); // no markOnboarded → server clears onboardedAt
  state.setServer(confirmed);
  return confirmed;
}

/** The layout the dashboard should RENDER: the draft while editing, else server. */
export function activeLayout(state: DashboardStoreState): DashboardLayout | null {
  return state.editing ? state.draft : state.server;
}
